import { defineStore } from 'pinia';
import type { Market } from '~/constants/types/market';
import type { NavigationType } from '~/constants/types/navigation';
import type { ShippingMethod } from '~/constants/types/shipping-method';
import type { Club } from '~/constants/types/club';
import { trailingSlashit } from '~/util/strings';

export type YotpoReview = {
  domain_key: string;
  product_score: number;
  total_reviews: number;
};

type Breadcrumb = {
  title: string;
  route: string;
};

type Route = {
  id: number;
  title: string;
  type: string;
  route: string;
  ecom_id?: number;
  template: string;
  main_product_list?: boolean;
  ecom_attribute?: {
    pr_gender: string[];
    pr_type: string[];
  };
  order: number;
  breadcrumbs: Breadcrumb[],
  is_startpage: boolean,
};

type SocialMediaAccounts = 'facebook' | 'instagram' | 'twitter' | 'youtube';

type Newsletter = {
  newsletter_title: string;
  newsletter_content: string;
  newsletter_thank_you_text: string;
};

type ConfigApiResponse = {
  cart_cross_sell: { products: string[]; title: string; },
  checkout: { text: string; title: string; },
  cookie_text: string | null,
  default_club: Club | null,
  delivery_information: string | null,
  fetched: false,
  footer_text: string,
  home_url: string,
  market: Market,
  markets: Market[],
  myPagesContent: string | null, // ? - does not load from backend
  myPagesHeadline: string | null, // ?
  navigation: { primary_navigation: NavigationType[],footer_navigation: NavigationType[],flyout_navigation: NavigationType[], },
  newsletter: Newsletter | null,
  search_popular: string[],
  routes: Route[],
  shipping_methods: ShippingMethod[],
  site_wide_message: string | null,
  size_information: string | null,
  social_accounts: { [name in SocialMediaAccounts]: string;}[],
  translations: {
    [name: string]: {
      [name: string]: string;
    };
  } | null,
  usps: { usp:string }[],
  validation_error: string | null,
  yotpo_reviews: YotpoReview[],
}

export const useConfigStore = defineStore('configStore', {
  state: () => ({
    market: {} as Market,
    markets: [] as Market[],
    home_url: '' as string,
    routes: [] as Route[],
    footer_text: '' as string,
    navigation: {
      primary_navigation: [] as NavigationType[],
      footer_navigation: [] as NavigationType[],
      flyout_navigation: [] as NavigationType[],
    },
    fetched: false,
    social_accounts: [] as {
      [name in SocialMediaAccounts]: string;
    }[],
    myPagesContent: '' as string | null,
    myPagesHeadline: '' as string | null,
    siteWideMessage: '' as string | null,
    cookieText: '' as string | null,
    newsletter: {} as Newsletter | null,
    shippingMethods: [] as ShippingMethod[],
    validationError: '' as string | null,
    popularSearches: [] as string[],
    translations: null as {
      [name: string]: {
        [name: string]: string;
      };
    } | null,
    cart_cross_sell: {} as {
      products: string[];
      title: string;
    },
    checkout: {} as {
      text: string;
      title: string;
    },
    sizeInformation: '' as string | null,
    deliveryInformation: '' as string | null,
    usps: [] as { usp:string }[],
    default_club: {} as Club | null,
    yotpo_reviews: [] as YotpoReview[],
    clubCookie: {} as {
      RoundID: string;
      CustNo: string;
      Name: string;
      City: string;
      Picture: string;
      Active: boolean,
      DeadlineDate: string;
      NoDeadlineDate: boolean,
      objectID: string;
    },
    sellerCookie: {} as {
      objectId: string;
      WebshopToken: string;
      Name: string;
      WebshopID: string;
      Phone: string;
      Email: string;
      RoundID: string;
      hidden: number;
    },
    isSeller: false,
  }),
  actions: {
    async loadConfig() {
      this.home_url = 'test';
      const { apiGet, lastErrorCode } = useApiFetch();

      const res = await apiGet<ConfigApiResponse>('/wp-json/api/v1/navigation-map');

      if (res) {
        const formattedRoutes = Object.entries(res.routes).map((r) => {
          return {
            ...r[1],
            route: r[0],
          };
        });
        // changed
        this.fetched = true;
        this.usps = res.usps || [];
        this.routes = formattedRoutes;

        // as is
        this.cart_cross_sell = res.cart_cross_sell;
        this.checkout = res.checkout;
        this.cookieText = res.cookie_text;
        this.default_club = res.default_club;
        this.deliveryInformation = res.delivery_information;
        this.footer_text = res.footer_text;
        this.home_url = res.home_url;
        this.market = res.market;
        this.markets = res.markets;
        this.myPagesContent = res.myPagesContent;
        this.myPagesHeadline = res.myPagesHeadline;
        this.navigation = res.navigation;
        this.newsletter = res.newsletter;
        this.popularSearches = res.search_popular;
        this.shippingMethods = res.shipping_methods;
        this.siteWideMessage = res.site_wide_message;
        this.sizeInformation = res.size_information;
        this.social_accounts = res.social_accounts;
        this.translations = res.translations;
        this.validationError = res.validation_error;
        this.yotpo_reviews = res.yotpo_reviews;
      } else {
        console.error('[CONFIG STORE] Failed to load navigation-map, routes will be empty:', {
          errorCode: lastErrorCode.value,
          error: lastError.value,
        });
      }

    },
  },
  getters: {
    startPageUrls: (state) => state.markets.map(m => m.url),
    getRouteIdFromPath: state => (route: string) => {
      // allows with and without trailing slash
      const find = state.routes.find(f => f.route === route || f.route === route +'/');
      if (find) {
        return find.id;
      }
      return -1;
    },
    routeData: state => (endpoint: string) => {
      if (!endpoint || !endpoint.length || endpoint === '/') {
        endpoint = state.home_url;
      }
      return state.routes.find((r) => r.route === trailingSlashit(endpoint));
    },
    getProductRoute: state => (ecomId: number) => {
      return state.routes.find((r) => {
        const routeEcomId = r.ecom_id;
        return routeEcomId === ecomId;
      });
    },
    findGenderProductList: (state) => (gender: string) => {
      return state.routes.find((r) => {
        const attr = r.ecom_attribute;
        if (attr && attr.pr_gender && attr.pr_gender[0] === gender && ((attr.pr_type && attr.pr_type.length === 0) || !attr.pr_type)) {
          return true;
        } else {
          return false;
        }
      });
    },
    findTypeProductList: (state) => (gender: string, type: string) => {
      return state.routes.find((r) => {
        const attr = r.ecom_attribute;
        if (attr && attr.pr_gender && attr.pr_gender[0] === gender && attr.pr_type && attr.pr_type[0] === type) {
          return true;
        } else {
          return false;
        }
      });
    },
    yotpoReviewByProductSku: (state) => (sku: string | undefined): YotpoReview | undefined => {
      return state.yotpo_reviews.find((r) => {
        return r.domain_key === sku;
      });
    },
    pageByTemplate: (state) => (template: string) => {
      return state.routes.find((r) => r.template === template);
    },
    checkoutRoute: (state) => {
      const routeData = state.routes.find((r) => r.template === 'template-checkout');
      if (routeData) {
        return routeData.route;
      }
      return '';
    },
    pageName: (state) => {
      const thisMarket = state.markets.find(f => f.market === state.market.market);
      if (thisMarket) {
        return thisMarket.name;
      }
      return 'Ullmax';
    },
    algoliaBaseIndex: (state) => {
      const runtimeConfig = useRuntimeConfig();
      console.log('country: '+state.market.country);
      let prefix = '';
      if (state.market.country) {
        prefix = `${runtimeConfig.public.algoliaIndexPrefix}${state.market.country.toLowerCase()}`;
        if (state.market.language.toLowerCase() === 'fi-sv' && state.market.country.toLowerCase() === 'fi') {
          prefix += '-sv';
        }
      } else {
        prefix = 'ullmax_stage_se';
      }
      return prefix;
    },
    getSupportLinks: (state) => () => {
      return state.routes.filter((r) => r.template === 'template-support-page');
    },
    getSupportSalesLinks: (state) => () => {
      return state.routes.filter((r) => r.template === 'template-support-page-sales');
    },
    pageById: (state) => (id: number): Route | undefined => {
      return state.routes.find((r) => r.id === id);
    },
  },
});
